'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FooterLink {
  id: string;
  label: string;
  href: string;
}

const footerLinks: FooterLink[] = [
  { id: 'privacy', label: 'Personvern', href: '/personvern' },
  { id: 'terms', label: 'Vilkår', href: '/vilkar' },
  { id: 'cookies', label: 'Informasjonskapsler', href: '#' },
  { id: 'about', label: 'Om oss', href: '/about-us-page' },
];

const FooterSection = () => {
  const [currentYear, setCurrentYear] = useState<number>(2026);
  const [isHydrated, setIsHydrated] = useState<boolean>(false);

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date().getFullYear());
  }, []);

  if (!isHydrated) {
    return (
      <footer className="bg-primary py-12 text-primary-foreground">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-4">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                  <span className="font-headline text-2xl font-bold text-accent-foreground">G</span>
                </div>
                <span className="font-headline text-2xl font-semibold">Gullklar</span>
              </div>
              <p className="mt-4 font-body text-sm text-primary-foreground/80">
                Norges mest transparente gullkjøp. Vi tilbyr rettferdig pris, trygg prosess, og garantert tilfredshet.
              </p>
            </div>
            <div>
              <h3 className="font-headline text-lg font-semibold">Kontakt</h3>
              <div className="mt-4 space-y-2 font-body text-sm text-primary-foreground/80">
                <p>E-post: kontakt@gullklar.no</p>
              </div>
            </div>
            <div>
              <h3 className="font-headline text-lg font-semibold">Adresse</h3>
              <div className="mt-4 space-y-1 font-body text-sm text-primary-foreground/80">
                <p>Gullklar</p>
                <p>Oslo</p>
              </div>
            </div>
          </div>
          <div className="mt-8 border-t border-primary-foreground/20 pt-8">
            <div className="flex flex-col items-center justify-between space-y-4 lg:flex-row lg:space-y-0">
              <p className="font-body text-sm text-primary-foreground/80">
                &copy; 2026 Gullklar. Alle rettigheter reservert.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                {footerLinks.map((link) => (
                  <a
                    key={link.id}
                    href={link.href}
                    className="font-body text-sm text-primary-foreground/80 transition-colors duration-250 hover:text-accent"
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-primary py-12 text-primary-foreground">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent">
                <span className="font-headline text-2xl font-bold text-accent-foreground">G</span>
              </div>
              <span className="font-headline text-2xl font-semibold">Gullklar</span>
            </div>
            <p className="mt-4 font-body text-sm text-primary-foreground/80 lg:text-base">
              Norges mest transparente gullkjøp. Vi tilbyr rettferdig pris, trygg prosess, og garantert tilfredshet.
            </p>
            <div className="mt-6 flex space-x-4">
              <a
                href="#"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 transition-colors duration-250 hover:bg-accent"
                aria-label="Facebook"
              >
                <Icon name="ShareIcon" size={20} variant="outline" />
              </a>
              <a
                href="#"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 transition-colors duration-250 hover:bg-accent"
                aria-label="Instagram"
              >
                <Icon name="CameraIcon" size={20} variant="outline" />
              </a>
              <a
                href="#"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-foreground/10 transition-colors duration-250 hover:bg-accent"
                aria-label="LinkedIn"
              >
                <Icon name="BriefcaseIcon" size={20} variant="outline" />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-headline text-lg font-semibold">Kontakt</h3>
            <div className="mt-4 space-y-3 font-body text-sm text-primary-foreground/80">
              <a href="mailto:kontakt@gullklar.no" className="flex items-center space-x-2 transition-colors duration-250 hover:text-accent">
                <Icon name="EnvelopeIcon" size={16} variant="solid" />
                <span>kontakt@gullklar.no</span>
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-headline text-lg font-semibold">Adresse</h3>
            <div className="mt-4 space-y-1 font-body text-sm text-primary-foreground/80">
              <p>Gullklar</p>
              <p>Oslo</p>
            </div>
          </div>
        </div>

        <div className="mt-8 border-t border-primary-foreground/20 pt-8">
          <div className="flex flex-col items-center justify-between space-y-4 lg:flex-row lg:space-y-0">
            <p className="font-body text-sm text-primary-foreground/80">
              &copy; {currentYear} Gullklar. Alle rettigheter reservert.
            </p>
            <div className="flex flex-wrap justify-center gap-4 lg:gap-6">
              {footerLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  className="font-body text-sm text-primary-foreground/80 transition-colors duration-250 hover:text-accent"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterSection;